/**
 * Thrown when a guarded desk write changed nothing: the RPC answered `false`
 * (or `null` for the RPCs that return a stamp or a tab: `desk_reject`,
 * `desk_attach`, `desk_dismiss_match`, `desk_decide_update`, `desk_restore`,
 * `desk_undo`) because the row was already decided, by
 * another tab or another person (D-11). A write that did not happen is never
 * shown as a success (T-33-47).
 *
 * The `name` string is the whole contract with `pages/Desk/deskErrors.ts`
 * (`DESK_ALREADY_DECIDED`): there is no import between the two files. The
 * message names the RPC only, never user data.
 */
export class DeskAlreadyDecidedError extends Error {
  constructor(rpc: string) {
    super(`${rpc}: already decided`)
    this.name = 'DeskAlreadyDecidedError'
  }
}

/** Throw DeskAlreadyDecidedError unless the guarded RPC reported a change. */
export const requireDecided = (rpc: string, changed: boolean | null | undefined): true => {
  if (changed !== true) {
    throw new DeskAlreadyDecidedError(rpc)
  }
  return true
}

/**
 * The decision RPCs that return the stamp they wrote (`desk_reject`,
 * `desk_attach`, `desk_dismiss_match`, `desk_decide_update`, review AD-WR-11):
 * a non-empty string is the stamp, JSON null means nothing changed.
 *
 * The stamp is returned exactly as PostgREST sent it (microseconds included).
 * It is `desk_undo`'s guard, so it must never go through a JS Date, which
 * keeps milliseconds only and would make the undo never match.
 */
export const requireStamp = (rpc: string, stamp: string | null | undefined): string => {
  if (typeof stamp !== 'string' || stamp === '') {
    throw new DeskAlreadyDecidedError(rpc)
  }
  return stamp
}
